import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { DEFAULT_PAGE_SIZE } from '../../commons/constants/constants';
import {
  createTourSound,
  deleteTourSound,
  updateSubtitle,
  updateTourSound,
} from '../../services/tourGuide.service';
import { storeName as tourGuideEntityStoreName } from '../entities/tourGuide.entity';
import { uploadFiles } from '../../services/uploader.service';
import { TourSoundGenreEnum } from '../../types/TourSoundGenreEnum';
import { TourSoundTypeEnum } from '../../types/TourSoundTypeEnum';
import { RootModel } from '../models';
import { PageRequest } from '../../types/CommonTypes';

export const storeName = 'tourGuides';

const initialState = {
  ids: [],
  pagination: {
    pageable: null,
    sort: null,
    totalPages: 0,
    totalElements: 0,
    last: false,
    first: true,
    numberOfElements: 0,
    size: DEFAULT_PAGE_SIZE,
    number: 0,
    empty: null,
  },
  loading: false,
  mp3ModalVisible: false,
  textToSpeechModalVisible: false,
  editSubtitleModalVisible: false,
  previewTourGuide: null,
  editId: null,
  data: [],
  isPreviewTourGuide: false,
  criteria: {},
};

export const tourGuides: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setData: produce((state, payload) => {
      state.data = payload;
    }),
    setPreviewTourGuide: produce((state, payload) => {
      state.previewTourGuide = payload;
    }),
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setTextToSpeechModalVisible: produce((state, payload) => {
      state.textToSpeechModalVisible = payload;
    }),
    setMp3ModalVisible: produce((state, payload) => {
      state.mp3ModalVisible = payload;
    }),
    setEditSubtitleModalVisible: produce((state, payload) => {
      state.editSubtitleModalVisible = payload;
      if (!payload) {
        state.editId = null;
      }
    }),
    hideUploadMp3Modal: produce((state, payload) => {
      state.mp3ModalVisible = false;
      state.editId = null;
    }),
    setStatusPreviewTourGuide: produce((state, payload) => {
      state.isPreviewTourGuide = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof tourGuides>) => ({
    async reloadList(payload, state) {
      const { criteria, pagination } = state[storeName];
      await dispatch.tourGuides.loadTourGuides({ ...pagination, ...criteria });
    },
    async resetFilter(payload, state) {
      const { pagination } = state[storeName];
      await dispatch.tourGuides.loadTourGuides({ ...pagination, page: 0 });
    },
    async saveTourGuide(payload) {
      dispatch.tourGuides.setLoading(true);
      let result;
      try {
        const editing = !!payload.id;

        let uploadedSubtitle = payload?.uploadSubtitle?.subtitle;
        if (
          payload?.uploadSubtitle &&
          payload?.uploadSubtitle?.subtitle &&
          payload?.uploadSubtitle?.subtitle instanceof File
        ) {
          const fileResult = await uploadFiles(
            null,
            [payload.uploadSubtitle.subtitle],
            payload.uploadSubtitle.subtitle.name,
            payload.userId,
            'speechtotext'
          );
          if (fileResult.filesList.length > 0) {
            const fileStorage = fileResult.filesList[0];
            uploadedSubtitle = {
              id: fileStorage.id,
            };
          }
        }

        const params = { ...payload, uploadSubtitle: uploadedSubtitle };

        if (!editing) {
          result = await createTourSound(params);
          dispatch.tourGuides.resetFilter();
        } else {
          result = await updateTourSound(payload.id, params);
          dispatch.tourGuides.reloadList();
        }
        message.success('Save Tour Guide Successfully');
        return result;
      } catch (err) {
        message.error(`Save Tour Guide Failed: ${err.message}`);
        console.error(err.message);
        return err;
      } finally {
        dispatch.tourGuides.setLoading(false);
      }
    },
    async saveMp3TourGuide({ formData, userId }) {
      dispatch.tourGuides.setLoading(true);
      try {
        let uploadedSound = null;
        let uploadedSubtitle = formData?.uploadSubtitle?.subtitle;
        if (formData.audioFile instanceof File) {
          const fileResult = await uploadFiles(
            null,
            [formData.audioFile],
            'toursound',
            userId,
            'media'
          );
          if (fileResult.filesList.length > 0) {
            const fileStorage = fileResult.filesList[0];
            uploadedSound = {
              id: fileStorage.id,
            };
          }
        }

        if (
          formData?.uploadSubtitle &&
          formData?.uploadSubtitle?.subtitle &&
          formData?.uploadSubtitle?.subtitle instanceof File
        ) {
          const fileResult = await uploadFiles(
            null,
            [formData.uploadSubtitle.subtitle],
            formData.uploadSubtitle.subtitle.name,
            userId,
            'speechtotext'
          );
          if (fileResult.filesList.length > 0) {
            const fileStorage = fileResult.filesList[0];
            uploadedSubtitle = {
              id: fileStorage.id,
            };
          }
        }

        if (formData?.id) {
          await updateTourSound(formData.id, {
            ...formData,
            sound: uploadedSound,
            uploadSubtitle: uploadedSubtitle,
          });
          dispatch.tourGuides.reloadList();
        } else {
          await createTourSound({
            type: TourSoundTypeEnum.TOUR_GUIDE,
            genre: TourSoundGenreEnum.MP3_FILE,
            ...formData,
            sound: uploadedSound,
            uploadSubtitle: uploadedSubtitle,
          });
          dispatch.tourGuides.resetFilter();
        }

        message.success('Save Tour Guide Successfully');
      } catch (e) {
        console.error(e);
        message.error('Tour Guide Saving Failure!');
      } finally {
        dispatch.tourGuides.setLoading(false);
      }
    },
    async loadTourGuides(payload, state) {
      const params = {
        ...payload,
        page: payload ? payload.page : state[storeName].pagination.number,
        size: payload ? payload.size : state[storeName].pagination.size,
      };
      const data = await dispatch[tourGuideEntityStoreName].load(params);
      if (data && data.content) {
        this.setIds(data.content.map((tourGuide) => tourGuide.id));
        this.setPagination((({ content, ...otherProps }) => otherProps)(data));
        this.setCriteria(params);
      }
    },
    async deleteTourGuide(payload) {
      dispatch.tourGuides.setLoading(true);
      try {
        await deleteTourSound(payload);
        dispatch.tourGuides.reloadList();
        message.success('Success to delete tour guide!');
      } catch (e) {
        console.error(e);
        message.error('Fail to delete! Tour guide could be used in tour or scene!');
      } finally {
        dispatch.tourGuides.setLoading(false);
      }
    },

    async updateSubtitle({ id, payload }) {
      try {
        await updateSubtitle(id, payload);
        dispatch.tourGuides.loadTourGuides();
        message.success('Success to update subtitle!');
      } catch (e) {
        console.error(e);
        message.error('Fail to update subtitle!');
      }
    },
    async pagingAsync(payload: PageRequest, state) {
      const { criteria = {}, pagination = {} } = state[storeName];
      await this.loadTourGuides({ ...pagination, ...criteria, ...payload });
    },
  }),
});

export default tourGuides;
